import React, { useState, useEffect, useMemo, Fragment } from 'react';
import { Button, Form, Modal } from 'react-bootstrap';
import { BsPlusLg as PlusLg, BsPencil, BsTrash, BsGraphUp, BsGear } from 'react-icons/bs';
import { VscLoading } from 'react-icons/vsc';
import { toast } from 'react-toastify';
import Select from 'react-select';
import CreatableSelect from 'react-select/creatable';
import { BootstrapToolTip } from '@/_elements/ToolTip';
import { ActionStyles } from '@/components/Admin/reusableStyles';
import { TableCSS } from '../styles/MembersStyled';
import { getTeamIdFromURL } from '@/_helpers';
import {
    createCampaignAction,
    deleteCampaignAction,
    fetchCampaignsAction,
    updateCampaignAction,
    fetchVerticalsAction,
} from '@/_redux/actions/ActionCallCenter';
import { connect } from 'react-redux';
import ComponentErrorBoundary from '@/Layouts/ComponentErrorBoundary';
import Swal from 'sweetalert2';
import { callCenterApi } from '@/_services/api/callCenter';
import CampaignMetricsModal from './components/CampaignMetricsModal';
import QueueConfigModal from './components/QueueConfigModal';
import ActiveArchiveToggle from '@/components/Filters/ActiveArchiveToggle';

const getSortedItems = (items = []) => items.sort((a, b) => a?.name?.localeCompare(b?.name));
const truncateText = (text, length = 50) => {
    if (!text) return '';
    return text.length > length ? text.slice(0, length) + '...' : text;
};

const CampaignStatusBadge = ({ status }) => {
    const map = {
        draft: 'info',
        active: 'success',
        paused: 'warning',
    };
    const className = map[status] || 'danger';

    return <span className={`badge badge-${className}`}>{status}</span>;
};

const ItemRow = ({ sn, item, onEdit, onDelete, onViewMetrics, onEditConfig }) => {
    const isDraft = item?.status === 'draft';

    return (
        <tr key={item?._id}>
            <td>{sn}.</td>
            <td>{item?.name}</td>
            <td>{truncateText(item?.description, 50)}</td>
            <td>{(item?.contacts || []).length}</td>
            <td>{<CampaignStatusBadge status={item?.status} />}</td>
            <td>
                <div className="d-flex gap-2">
                    <ActionStyles>
                        <BootstrapToolTip text="Edit">
                            <BsPencil className={`icon edit ${!isDraft && 'disabled'}`} onClick={() => onEdit(item)} />
                        </BootstrapToolTip>
                        <BootstrapToolTip text="Delete">
                            <BsTrash
                                className={`icon delete ${!isDraft && 'disabled'}`}
                                onClick={() => onDelete(item)}
                            />
                        </BootstrapToolTip>
                        <BootstrapToolTip text="Queue Config">
                            <BsGear className={`icon blue `} onClick={() => onEditConfig(item)} />
                        </BootstrapToolTip>
                        {!isDraft && (
                            <BootstrapToolTip text="View Metrics">
                                <BsGraphUp className={`icon light-green`} onClick={() => onViewMetrics(item)} />
                            </BootstrapToolTip>
                        )}
                    </ActionStyles>
                </div>
            </td>
        </tr>
    );
};

const Component = ({
    contacts,
    presets,
    verticals,
    createCampaignAction,
    fetchCampaignsAction,
    updateCampaignAction,
    deleteCampaignAction,
    fetchVerticalsAction,
    setActiveTab,
}) => {
    const teamId = getTeamIdFromURL();
    const [isBusy, setIsBusy] = useState(false);
    const [items, setItems] = useState(null);
    const [filteredItems, setFilteredItems] = useState([]);
    const [searchText, setSearchText] = useState('');
    const [isArchived, setIsArchived] = useState(false);
    const [loading, setLoading] = useState(true);

    const [showModal, setShowModal] = useState(false);
    const [editingItem, setEditingItem] = useState(null);
    const [campaignData, setCampaignData] = useState({
        name: '',
        description: '',
        preset: null,
        contacts: [],
        vertical: null,
    });

    const [showMetricsModal, setShowMetricsModal] = useState(false);
    const [showConfigModal, setShowConfigModal] = useState(false);
    const [selectedCampaign, setSelectedCampaign] = useState(null);

    // Prepare contact options for react-select
    const contactOptions = useMemo(() => {
        return (contacts || []).map((contact) => {
            const hasPhone = contact?.phone?.trim();
            const phoneIcon = hasPhone ? '📞' : '⚠️';
            const phoneText = hasPhone ? contact.phone : 'No phone';

            return {
                value: contact._id,
                label: `${contact?.name || 'Unnamed'} | ${phoneIcon} ${phoneText}`,
                contact,
            };
        });
    }, [contacts]);

    // Prepare preset options for react-select
    const presetOptions = useMemo(() => {
        return (presets || []).map((preset) => ({
            value: preset._id,
            label: preset.title || `Preset ${preset._id}`,
            preset,
        }));
    }, [presets]);

    // Prepare vertical options for react-select
    const verticalOptions = useMemo(() => {
        return (verticals || []).map((vertical) => ({
            value: vertical._id,
            label: vertical.name || `Vertical ${vertical._id}`,
            vertical,
        }));
    }, [verticals]);

    const fetchCampaigns = async () => {
        try {
            if (!teamId) return;
            setLoading(true);
            const _items = (await fetchCampaignsAction(teamId, { archived: isArchived }))?.results;
            setItems(_items);
            setFilteredItems(_items);
            fetchVerticalsAction(teamId);
        } catch (e) {
            console.log('e', e);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchCampaigns();
    }, [teamId, isArchived]);

    const handleSearchChange = (e) => {
        const value = e.target.value.toLowerCase();
        setSearchText(value);
        if (value.trim() === '') {
            setFilteredItems(items);
        } else {
            const _filtered = items.filter(
                (p) =>
                    p.name?.toLowerCase().includes(value) ||
                    p.description?.toLowerCase().includes(value) ||
                    p.vertical?.name?.toLowerCase().includes(value)
            );
            setFilteredItems(_filtered);
        }
    };

    const createNewVertical = async (verticalName) => {
        try {
            const newVertical = await callCenterApi.createVertical({
                team: teamId,
                name: verticalName,
            });
            return newVertical;
        } catch (error) {
            console.error('Error creating vertical:', error);
            toast.error(error?.message || 'Failed to create new vertical');
            return null;
        }
    };

    const openCreateModal = () => {
        setEditingItem(null);
        setCampaignData({
            name: '',
            description: '',
            preset: null,
            contacts: [],
            vertical: null,
        });
        setShowModal(true);
    };

    const openEditModal = (data) => {
        setEditingItem(data);

        // Find selected preset if exists
        const selectedPreset = presetOptions.find((opt) => opt.value === data.preset);

        // Find selected contacts
        const selectedContacts = contactOptions.filter((opt) => data.contacts?.includes(opt.value));
        const selectedVertical = verticalOptions.find((opt) => opt.value === data.vertical);

        setCampaignData({
            name: data.name || '',
            description: data.description || '',
            preset: selectedPreset || null,
            contacts: selectedContacts || [],
            vertical: selectedVertical || null,
        });
        setShowModal(true);
    };

    const handleSave = async () => {
        // Validation
        if (!campaignData.preset) {
            toast.error('Please select a preset');
            return;
        }

        if (campaignData.contacts.length === 0) {
            toast.error('Please select at least one contact');
            return;
        }

        setIsBusy(true);
        let verticalId = (campaignData.vertical || {}).value;
        if (campaignData?.vertical && campaignData?.vertical?.__isNew__) {
            const verticalName = verticalId.replace('create:', '');
            const newVertical = await createNewVertical(verticalName);
            if (newVertical) {
                verticalId = newVertical._id;
            }
        }

        const payload = {
            name: campaignData.name,
            description: campaignData.description,
            preset: campaignData.preset?.value,
            contacts: campaignData.contacts.map((contact) => contact.value),
            vertical: verticalId,
        };
        try {
            if (editingItem) {
                await updateCampaignAction(editingItem._id, payload);
            } else {
                payload.team = teamId;
                await createCampaignAction(payload);
            }
            setShowModal(false);
            await fetchCampaigns(teamId, { archived: isArchived });
        } catch (err) {
            console.log('err', err);
            toast.error(err?.message || `Failed to ${editingItem ? 'update' : 'create'} campaign`);
        } finally {
            setIsBusy(false);
        }
    };

    const handleDelete = async (data) => {
        try {
            const result = await Swal.fire({
                title: `Are you sure you want to delete "${data.name}"? #Note: this cannot be undone`,
                icon: 'warning',
                showCancelButton: true,
                confirmButtonColor: '#3085d6',
                cancelButtonColor: '#d33',
                confirmButtonText: 'Yes, delete it!',
                cancelButtonText: 'Cancel',
            });

            if (result.isConfirmed) {
                await deleteCampaignAction(data._id);
                await fetchCampaigns(teamId, { archived: isArchived });
            }
        } catch (err) {
            toast.error(err?.message || 'Failed to delete campaign');
        }
    };

    const handleViewMetrics = (campaign) => {
        setSelectedCampaign(campaign);
        setShowMetricsModal(true);
    };

    const handleEditConfig = (campaign) => {
        setSelectedCampaign(campaign);
        setShowConfigModal(true);
    };

    // Custom styles for react-select
    const selectStyles = {
        control: (base) => ({
            ...base,
            border: '1px solid #ced4da',
            borderRadius: '0.375rem',
            '&:hover': {
                borderColor: '#86b7fe',
            },
            boxShadow: 'none',
        }),
        menu: (base) => ({
            ...base,
            zIndex: 9999,
        }),
    };

    const VerticalSelect = ({ value, onChange, options, onCreateOption }) => {
        const handleChange = (selectedOption) => {
            if (selectedOption?.isCreateOption) {
                const verticalName = selectedOption.value.replace('create:', '');
                onCreateOption(verticalName);
            } else {
                onChange(selectedOption);
            }
        };

        return (
            <CreatableSelect
                options={options}
                value={value}
                onChange={handleChange}
                placeholder="Search or create a vertical"
                isClearable
                isSearchable
                styles={selectStyles}
                required
                formatOptionLabel={(option) => {
                    if (option.isCreateOption) {
                        return (
                            <div style={{ color: '#2e7d32', fontWeight: 'bold' }}>
                                <PlusLg style={{ marginRight: '8px' }} />
                                {option.label}
                            </div>
                        );
                    }
                    return option.label;
                }}
            />
        );
    };

    return (
        <div>
            <div className="d-flex justify-content-between align-items-center mb-3">
                <input
                    className="form-control"
                    onChange={handleSearchChange}
                    value={searchText}
                    placeholder="Search campaigns..."
                    style={{
                        width: '250px',
                        border: '1px solid #ccc',
                        borderRadius: 6,
                        padding: '6px 10px',
                    }}
                />
                <div className="d-flex align-items-center">
                    <ActiveArchiveToggle
                        isActive={!isArchived}
                        setIsActive={(val) => setIsArchived(!val)}
                        disabled={loading}
                    />
                    <Button variant="primary" onClick={openCreateModal} className="ml-2">
                        <PlusLg /> New Campaign
                    </Button>
                </div>
            </div>

            {/* Table */}
            {loading ? (
                <div className="text-center py-5">
                    <VscLoading className="spin" fontSize="42" />
                </div>
            ) : (
                <div className="custom-card mt-3">
                    <TableCSS>
                        <thead>
                            <tr>
                                <th style={{ width: 10 }}>S.N.</th>
                                <th>Name</th>
                                <th>Description</th>
                                <th>Total Contacts</th>
                                <th>Status</th>
                                <th>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filteredItems.length > 0 ? (
                                filteredItems.map((item, index) => (
                                    <ItemRow
                                        sn={index + 1}
                                        key={item._id}
                                        item={item}
                                        onEdit={openEditModal}
                                        onDelete={handleDelete}
                                        onViewMetrics={handleViewMetrics}
                                        onEditConfig={handleEditConfig}
                                        setActiveTab={setActiveTab}
                                    />
                                ))
                            ) : (
                                <tr>
                                    <td colSpan="5">
                                        <div className="text-center py-4">
                                            <h3 className="text-muted mb-0">
                                                No {isArchived ? 'Archived ' : 'Active '}Campaigns Found
                                            </h3>
                                        </div>
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </TableCSS>
                </div>
            )}

            {/* Modal */}
            <Modal show={showModal} onHide={() => setShowModal(false)} size="lg" centered>
                <Modal.Header closeButton>
                    <Modal.Title>{editingItem ? 'Edit Campaign' : 'New Campaign'}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Form.Group className="mb-3">
                        <Form.Label>
                            Name <span className="text-danger">*</span>
                        </Form.Label>
                        <Form.Control
                            value={campaignData.name}
                            onChange={(e) => setCampaignData({ ...campaignData, name: e.target.value })}
                            placeholder="Enter campaign name"
                            required
                        />
                    </Form.Group>

                    <Form.Group className="mb-3">
                        <Form.Label>Description</Form.Label>
                        <Form.Control
                            as="textarea"
                            rows={3}
                            value={campaignData.description}
                            onChange={(e) => setCampaignData({ ...campaignData, description: e.target.value })}
                            placeholder="Enter campaign description"
                        />
                    </Form.Group>

                    <Form.Group className="mb-3">
                        <Form.Label>
                            Vertical
                            {/* <span className="text-danger">*</span> */}
                        </Form.Label>
                        <VerticalSelect
                            value={campaignData.vertical}
                            onChange={(selected) => setCampaignData({ ...campaignData, vertical: selected })}
                            options={verticalOptions}
                            onCreateOption={(verticalName) => {
                                setCampaignData({
                                    ...campaignData,
                                    vertical: {
                                        value: `create:${verticalName}`,
                                        label: `Create new: "${verticalName}"`,
                                        isCreateOption: true,
                                    },
                                });
                            }}
                        />
                        <Form.Text className="text-muted">
                            Type to search existing verticals or create a new one
                        </Form.Text>
                    </Form.Group>

                    {/* Preset Selection */}
                    <Form.Group className="mb-3">
                        <Form.Label>
                            Preset <span className="text-danger">*</span>
                        </Form.Label>
                        <Select
                            options={presetOptions}
                            value={campaignData.preset}
                            onChange={(selected) => setCampaignData({ ...campaignData, preset: selected })}
                            placeholder="Select a preset"
                            isClearable
                            isSearchable
                            styles={selectStyles}
                            required
                        />
                    </Form.Group>

                    {/* Contacts Selection */}
                    <Form.Group className="mb-3">
                        <Form.Label>
                            Contacts <span className="text-danger">*</span>
                        </Form.Label>
                        <Select
                            options={contactOptions}
                            value={campaignData.contacts}
                            onChange={(selected) => setCampaignData({ ...campaignData, contacts: selected || [] })}
                            placeholder="Select contacts"
                            isMulti
                            isClearable
                            isSearchable
                            styles={selectStyles}
                            required
                        />
                    </Form.Group>
                </Modal.Body>

                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowModal(false)} disabled={isBusy}>
                        Cancel
                    </Button>
                    <Button variant="primary" onClick={handleSave} disabled={isBusy}>
                        {isBusy && <VscLoading className="spin mr-2" />}
                        {editingItem ? 'Update Campaign' : 'Create Campaign'}
                    </Button>
                </Modal.Footer>
            </Modal>

            <CampaignMetricsModal
                show={showMetricsModal}
                onHide={() => setShowMetricsModal(false)}
                campaignId={selectedCampaign?._id}
                campaignName={selectedCampaign?.name}
            />
            <QueueConfigModal
                show={showConfigModal}
                onHide={() => setShowConfigModal(false)}
                campaignId={selectedCampaign?._id}
                campaignName={selectedCampaign?.name}
                campaign={selectedCampaign}
            />
        </div>
    );
};

const mapStateToProps = (state) => ({
    // campaigns: state.callCenter.campaigns || [],
    contacts: state.callCenter.contacts || [],
    presets: state.callCenter?.presets || [],
    verticals: state.callCenter?.verticals || [],
});

const mapDispatchToProps = {
    fetchCampaignsAction,
    createCampaignAction,
    updateCampaignAction,
    deleteCampaignAction,
    fetchVerticalsAction,
};

const CampaignsTab = (props) => (
    <ComponentErrorBoundary componentErrorMessage={props?.componentErrorMessage}>
        <Component {...props} />
    </ComponentErrorBoundary>
);

export default connect(mapStateToProps, mapDispatchToProps)(CampaignsTab);
